import { QueryKey, useQueryClient } from '@tanstack/react-query';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FormatListBulleted } from '@mui/icons-material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ChatIcon from '@mui/icons-material/Chat';
import InfoIcon from '@mui/icons-material/Info';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';

import { BoxResizeHandle, IconComponent, useAsyncMemo } from '@@common';

import { BundleDefinitionModel, ComponentMetadataModel, ReactCodeComponentModel } from '../../../api/api';
import ActionPanelTabContent from '../../../components/ActionPanelTabContent';
import PromptChat, { PromptChatStatus } from '../../../components/Chat/PromptChat';
import AppCheckpointActions from './AppCheckpointActions';
import { ChatMessage } from '../../../components/Chat/PromptChatAdapter';
import { getReactChangeBuildId, isChangeChatMessage } from '../../../components/Chat/isChangeChatMessage';
import ListWithFilter from '../../FlowDesigner/Components/ListWithFilter';
import useFlowQueries from '../../FlowDesigner/Hooks/useFlowQueries';
import { UserInterfaceChatArtifactContext, useUserInterfaceChatAdapter } from '../Hooks/useUserInterfaceChatAdapter';
import useUserInterfaceMetadata from '../Hooks/useUserInterfaceMetadata';
import { Bundle } from '../Models/Bundle';
import { ComponentTypes } from '../Models/ComponentTypes';
import { IComponentModel } from '../Models/IComponentModel';
import { IContainerComponentModel } from '../Models/IContainerComponentModel';
import { ReactBundleModel } from '../Models/ReactBundleModel';
import { SharedComponentModel } from '../Models/SharedComponentModel';
import { ViewModel } from '../Models/ViewModel';
import { ViewVariableScopeCalculatorService } from '../Services/ViewVariableScopeCalculatorService';
import { ComponentPath, findClosestContainerPath, getComponentAtPath, resolveSharedComponentSelection } from '../Utils/ComponentTreeUtils';
import { getSelectedComponentArtifactContext } from '../Utils/ReactComponentSelectionUtils';
import { getComponentTreePath } from '../Utils/VirtualComponentCollectionUtils';
import {
	ITEM_TYPE_EMBEDDED_RESOURCE,
	ITEM_TYPE_REACT_SOURCE_FILE,
	ITEM_TYPE_SETTINGS,
	ITEM_TYPE_SHARED_COMPONENT,
	ITEM_TYPE_THEME,
	ReactSourceFileArtifact,
	ITEM_TYPE_VIEW,
	SelectedItem,
	SharedComponentSelectionContext,
} from './AppArtifactPanel';
import { AppItemProperties } from './AppItemProperties';

const LOCAL_STORAGE_KEY = 'uiActionPanel.width';
const DEFAULT_WIDTH = 300;
const MIN_WIDTH = 200;
const MAX_WIDTH = 800;
const USER_INTERFACE_CHAT_SUGGESTED_PROMPTS = ['Implement the selected view in React', 'Update the selected React component', 'Match my brand'];

export const SECTION_PROPERTIES = 'properties' as const;
export const SECTION_ADD_COMPONENT = 'addComponent' as const;
export const SECTION_CHAT = 'chat' as const;

type SectionType = typeof SECTION_PROPERTIES | typeof SECTION_ADD_COMPONENT | typeof SECTION_CHAT;

type ActionPanelProps = {
	definitionId?: string;
	bundleQueryKey: QueryKey;
	selectedItem: SelectedItem | null;
	bundle: Bundle | null;
	reactComponents?: ReactCodeComponentModel[];
	changeSetId?: string | null;
	selectedComponentPath: ComponentPath | null;
	selectedSharedComponentContext?: SharedComponentSelectionContext | null;
	onBundleChanged: (bundle: Bundle) => void;
	onComponentChanged?: (updated: IComponentModel) => void;
	onAddComponent: (component: ComponentMetadataModel) => void;
	onMoveComponentUp?: () => void;
	onMoveComponentDown?: () => void;
	onRemoveComponent?: () => void;
	onSelectedComponentPathChanged?: (path: ComponentPath | null) => void;
	onReactBuildChanged?: (buildId: string, reactBundle: ReactBundleModel) => void;
	selectedReactSourceFile?: ReactSourceFileArtifact | null;
	isMobile?: boolean;
	mobileChatMode?: boolean;
	readOnly?: boolean;
	hasUnsavedChanges?: boolean;
};

export function ActionPanel({
	definitionId,
	bundleQueryKey,
	selectedItem,
	bundle,
	reactComponents = [],
	selectedComponentPath,
	selectedSharedComponentContext,
	onBundleChanged,
	onComponentChanged,
	onAddComponent,
	changeSetId,
	onMoveComponentUp,
	onMoveComponentDown,
	onRemoveComponent,
	onSelectedComponentPathChanged,
	onReactBuildChanged,
	selectedReactSourceFile,
	isMobile = false,
	mobileChatMode = false,
	readOnly = false,
	hasUnsavedChanges = false,
}: ActionPanelProps) {
	const [chatBusy, setChatBusy] = useState(false);
	const handleChatStatus = useCallback((status: PromptChatStatus) => setChatBusy(status.busy), []);
	const [panelWidth, setPanelWidth] = useState<number>(DEFAULT_WIDTH);
	const panelRef = useRef<HTMLDivElement>(null);
	const [activeSection, setActiveSection] = useState<SectionType>(SECTION_PROPERTIES);
	const isThemeSelected = selectedItem?.type === ITEM_TYPE_THEME;
	const isReactSourceFileSelected = selectedItem?.type === ITEM_TYPE_REACT_SOURCE_FILE;
	const isChatOnlySelection = !readOnly && (isThemeSelected || isReactSourceFileSelected);
	const canShowAddComponent = !isChatOnlySelection && selectedItem?.type !== ITEM_TYPE_EMBEDDED_RESOURCE && selectedItem?.type !== ITEM_TYPE_SETTINGS;
	const displayedActiveSection = readOnly
		? SECTION_PROPERTIES
		: mobileChatMode
		? SECTION_CHAT
		: isChatOnlySelection
		? SECTION_CHAT
		: !canShowAddComponent && activeSection === SECTION_ADD_COMPONENT
			? SECTION_PROPERTIES
			: activeSection;

	const selectedComponentArtifactContext = useMemo(
		() => getSelectedComponentArtifactContext(bundle, selectedItem, selectedComponentPath, selectedSharedComponentContext, reactComponents),
		[bundle, reactComponents, selectedComponentPath, selectedItem, selectedSharedComponentContext],
	);
	const selectedReactComponentName = selectedComponentArtifactContext.reactComponentName;
	const selectedReactSourceDescriptor = useMemo(
		() => reactComponents.find((component) => component.entryPoint === selectedReactSourceFile?.path),
		[reactComponents, selectedReactSourceFile?.path],
	);

	const chatArtifactContext = useMemo<UserInterfaceChatArtifactContext>(() => {
		if (!selectedItem) {
			return {};
		}

		if (selectedItem.type === ITEM_TYPE_VIEW) {
			return { viewId: selectedItem.id, ...selectedComponentArtifactContext };
		}

		if (selectedItem.type === ITEM_TYPE_SHARED_COMPONENT) {
			return { sharedComponentId: selectedItem.id, ...selectedComponentArtifactContext };
		}

		if (selectedItem.type === ITEM_TYPE_EMBEDDED_RESOURCE) {
			return { resourceId: selectedItem.id };
		}

		if (selectedItem.type === ITEM_TYPE_THEME) {
			const themeName = bundle?.Themes?.find((theme) => theme.Id === selectedItem.id)?.Name;
			return { area: 'App-Theme', themeName };
		}

		if (selectedItem.type === ITEM_TYPE_SETTINGS) {
			return { area: 'App-Settings' };
		}

		if (selectedItem.type === ITEM_TYPE_REACT_SOURCE_FILE && selectedReactSourceFile) {
			return {
				reactSourceFileId: selectedReactSourceFile.id,
				reactSourceFilePath: selectedReactSourceFile.path,
				...(selectedReactSourceDescriptor ? { reactComponentId: selectedReactSourceDescriptor.id, reactComponentName: selectedReactSourceDescriptor.name } : {}),
			};
		}

		return {};
	}, [bundle?.Themes, selectedComponentArtifactContext, selectedItem, selectedReactSourceDescriptor, selectedReactSourceFile]);

	const queryClient = useQueryClient();
	const refreshCheckpoints = useCallback(async () => {
		await Promise.all([
			queryClient.invalidateQueries({ queryKey: ['userinterface', 'checkpoints', definitionId] }),
			queryClient.invalidateQueries({ queryKey: ['userinterface', 'checkpoint-comparison', definitionId] }),
		]);
	}, [definitionId, queryClient]);
	const handleCheckpointRestored = useCallback(async () => {
		await Promise.all([
			queryClient.refetchQueries({ queryKey: bundleQueryKey, exact: true }, { throwOnError: true }),
			queryClient.refetchQueries({ queryKey: ['userinterface', 'react-source-files', definitionId], type: 'all' }, { throwOnError: true }),
			queryClient.refetchQueries({ queryKey: ['userinterface', 'react-source-file', definitionId], type: 'all' }, { throwOnError: true }),
			queryClient.refetchQueries({ queryKey: ['userinterface', 'react-code-status', definitionId], type: 'all' }, { throwOnError: true }),
		]);
		const record = queryClient.getQueryData<BundleDefinitionModel>(bundleQueryKey);
		const restored = record ? JSON.parse(record.definition) as Bundle : undefined;
		if (restored?.ReactBundle?.BuildId) {
			onReactBuildChanged?.(restored.ReactBundle.BuildId, restored.ReactBundle);
		}
	}, [bundleQueryKey, definitionId, onReactBuildChanged, queryClient]);
	const chatAdapter = useUserInterfaceChatAdapter(bundle, changeSetId, chatArtifactContext);
	const handleAssistantMessageReceived = useCallback(
		async (message: ChatMessage) => {
			await refreshCheckpoints();
			const buildId = getReactChangeBuildId(message);
			if (!definitionId || hasUnsavedChanges || (!isChangeChatMessage(message) && !buildId)) {
				return;
			}

			await Promise.all([
				queryClient.refetchQueries({ queryKey: bundleQueryKey, exact: true }),
				queryClient.refetchQueries({ queryKey: ['userinterface', 'react-source-files', definitionId], type: 'all' }),
				queryClient.refetchQueries({ queryKey: ['userinterface', 'react-source-file', definitionId], type: 'all' }),
				queryClient.refetchQueries({ queryKey: ['userinterface', 'react-code-status', definitionId], type: 'all' }),
			]);
			if (buildId) {
				const refreshedDefinition = queryClient.getQueryData<BundleDefinitionModel>(bundleQueryKey);
				const refreshedBundle = refreshedDefinition ? (JSON.parse(refreshedDefinition.definition) as Bundle) : undefined;
				if (refreshedBundle?.ReactBundle?.BuildId === buildId) {
					onReactBuildChanged?.(buildId, refreshedBundle.ReactBundle);
				}
			}
		},
		[bundleQueryKey, definitionId, hasUnsavedChanges, onReactBuildChanged, queryClient, refreshCheckpoints],
	);

	const { componentByName, expressionMetadataList } = useUserInterfaceMetadata();
	// TODO: Refactor VariableScopeCalculatorServiceBase to not depend on FlowQueries —
	// the UI designer does not use flow queries, but the base class requires them.
	const flowQueries = useFlowQueries();
	const components = useMemo(
		() => Array.from(componentByName.values()).filter((c) => c.name !== ComponentTypes.View && c.isVirtual !== true),
		[componentByName],
	);

	// Compute the variable scope visible at the selected component's position in the view tree.
	// For views, this includes $view and $input (route params).
	// For shared components, this includes just $view.
	const selectedView = useMemo<ViewModel | null>(() => {
		if (!bundle || !selectedItem || selectedItem.type !== ITEM_TYPE_VIEW) return null;
		return bundle.Views.find((v) => v.Id === selectedItem.id) ?? null;
	}, [bundle, selectedItem]);

	const selectedSharedComponent = useMemo<SharedComponentModel | null>(() => {
		if (!bundle || !selectedItem || selectedItem.type !== ITEM_TYPE_SHARED_COMPONENT) return null;
		return bundle.SharedComponents.find((c) => c.Id === selectedItem.id) ?? null;
	}, [bundle, selectedItem]);

	const variableScope = useAsyncMemo(async () => {
		const componentTreePath = getComponentTreePath(selectedComponentPath);

		if (selectedView) {
			const service = new ViewVariableScopeCalculatorService(componentByName, flowQueries, queryClient, expressionMetadataList, bundle);
			return service.getVariableScopeForView(selectedView, componentTreePath);
		}

		if (selectedSharedComponent) {
			const service = new ViewVariableScopeCalculatorService(componentByName, flowQueries, queryClient, expressionMetadataList, bundle);
			return service.getVariableScopeForSharedComponent(selectedSharedComponent, componentTreePath);
		}

		return undefined;
	}, [selectedView, selectedSharedComponent, componentByName, expressionMetadataList, flowQueries, queryClient, selectedComponentPath, bundle]);

	// Determine where new components will be added
	const addTargetInfo = useMemo(() => {
		if (!bundle || !selectedItem) {
			return { targetName: '', targetType: '', canAdd: false, isRoot: false };
		}

		if (selectedItem.type !== ITEM_TYPE_VIEW && selectedItem.type !== ITEM_TYPE_SHARED_COMPONENT) {
			return { targetName: '', targetType: '', canAdd: false, isRoot: false };
		}

		// If no component is selected, add to root
		let componentTreePath = getComponentTreePath(selectedComponentPath);

		if (selectedItem.type === ITEM_TYPE_VIEW && selectedSharedComponentContext) {
			const resolution = resolveSharedComponentSelection(bundle, selectedComponentPath, selectedSharedComponentContext);
			if (!resolution) {
				return { targetName: '', targetType: '', canAdd: false, isRoot: false };
			}

			componentTreePath = getComponentTreePath(resolution.pathInSharedComponent);
			const pathToUse = componentTreePath?.[0] === resolution.root.Id ? componentTreePath.slice(1) : (componentTreePath ?? []);

			if (!componentTreePath || pathToUse.length === 0) {
				const metadata = componentByName.get(resolution.root.Type);
				return {
					targetName: metadata?.displayName || resolution.root.Type,
					targetType: 'selected',
					canAdd: metadata?.isContainer === true,
					isRoot: false,
				};
			}

			const { containerPath, isRoot } = findClosestContainerPath(resolution.root, pathToUse, componentByName);

			if (isRoot) {
				return {
					targetName: 'Shared component root',
					targetType: 'root',
					canAdd: true,
					isRoot: true,
				};
			}

			const containerComponent = getComponentAtPath(resolution.root.Children, containerPath);
			if (!containerComponent) {
				return { targetName: '', targetType: '', canAdd: false, isRoot: false };
			}

			const metadata = componentByName.get(containerComponent.Type);
			const targetName = metadata?.displayName || containerComponent.Type;
			const addingToSelf = containerPath.join('/') === pathToUse.join('/');

			return {
				targetName,
				targetType: addingToSelf ? 'selected' : 'parent',
				canAdd: true,
				isRoot: false,
			};
		}

		if (!componentTreePath) {
			return {
				targetName: selectedItem.type === ITEM_TYPE_VIEW ? 'View root' : 'Shared component root',
				targetType: 'root',
				canAdd: true,
				isRoot: true,
			};
		}

		let view: IContainerComponentModel | null = null;

		if (selectedItem.type === ITEM_TYPE_VIEW) {
			const foundView = bundle.Views.find((v) => v.Id === selectedItem.id);
			if (foundView) view = foundView as unknown as IContainerComponentModel;
		} else if (selectedItem.type === ITEM_TYPE_SHARED_COMPONENT) {
			const sharedComponent = bundle.SharedComponents.find((c) => c.Id === selectedItem.id);
			if (sharedComponent) view = { ...sharedComponent.Component } as IContainerComponentModel;
		}

		if (!view) {
			return { targetName: '', targetType: '', canAdd: false, isRoot: false };
		}

		// For shared components, the path includes the root component ID as the first element
		// We need to strip it because we're searching within view.Children
		let pathToUse = componentTreePath;
		if (selectedItem.type === ITEM_TYPE_SHARED_COMPONENT && componentTreePath && componentTreePath[0] === view.Id) {
			pathToUse = componentTreePath.slice(1);
		}

		// If relative path is empty, we're selecting the root component
		if (selectedItem.type === ITEM_TYPE_SHARED_COMPONENT && (!pathToUse || pathToUse.length === 0)) {
			const metadata = componentByName.get(view.Type);
			if (metadata?.isContainer) {
				const targetName = metadata.displayName || view.Type;
				return {
					targetName,
					targetType: 'selected',
					canAdd: true,
					isRoot: false,
				};
			}
			return { targetName: '', targetType: '', canAdd: false, isRoot: false };
		}

		// Find the closest container path using the (potentially adjusted) path
		const { containerPath, isRoot } = findClosestContainerPath(view, pathToUse || [], componentByName);

		if (isRoot) {
			return {
				targetName: selectedItem.type === ITEM_TYPE_VIEW ? 'View root' : 'Shared component root',
				targetType: 'root',
				canAdd: true,
				isRoot: true,
			};
		}

		// Get the container component info
		const containerComponent = getComponentAtPath(view.Children, containerPath);
		if (!containerComponent) {
			return { targetName: '', targetType: '', canAdd: false, isRoot: false };
		}

		const metadata = componentByName.get(containerComponent.Type);
		const targetName = metadata?.displayName || containerComponent.Type;

		// Check if we're adding to the selected component itself or to a parent
		const addingToSelf = containerPath.join('/') === (pathToUse || []).join('/');

		return {
			targetName,
			targetType: addingToSelf ? 'selected' : 'parent',
			canAdd: true,
			isRoot: false,
		};
	}, [bundle, selectedComponentPath, selectedItem, selectedSharedComponentContext, componentByName]);

	useEffect(() => {
		setActiveSection(isChatOnlySelection ? SECTION_CHAT : SECTION_PROPERTIES);
	}, [isChatOnlySelection, selectedItem?.id, selectedItem?.type]);

	const handleTabChange = useCallback((_: React.SyntheticEvent, value: SectionType) => setActiveSection(value), []);

	return (
		<Paper
			ref={panelRef}
			sx={{
				position: 'relative',
				width: isMobile ? '100%' : `${panelWidth}px`,
				display: 'flex',
				flexDirection: 'column',
				minHeight: 0,
				flexShrink: 0,
			}}
		>
			{!isMobile && <BoxResizeHandle
				position="left"
				width={panelWidth}
				setWidth={setPanelWidth}
				panelRef={panelRef}
				storageKey={LOCAL_STORAGE_KEY}
				defaultWidth={DEFAULT_WIDTH}
				minWidth={MIN_WIDTH}
				maxWidth={MAX_WIDTH}
				direction="right"
			/>}

			<Stack direction="column" sx={{ flexGrow: 1, minHeight: 0 }}>
				{!mobileChatMode && <Tabs value={displayedActiveSection} onChange={handleTabChange} variant="fullWidth" sx={{ flexShrink: 0, borderBottom: 1, borderColor: 'divider' }}>
					{!isChatOnlySelection && (
						<Tab
							value={SECTION_PROPERTIES}
							icon={<FormatListBulleted fontSize="small" />}
							iconPosition="start"
							label="Properties"
							sx={{ minHeight: 48, fontSize: '0.75rem' }}
						/>
					)}
					{canShowAddComponent && (
						<Tab
							value={SECTION_ADD_COMPONENT}
							icon={<AddCircleIcon fontSize="small" />}
							iconPosition="start"
							label="Add Component"
							sx={{ minHeight: 48, fontSize: '0.75rem' }}
						/>
					)}
					{!readOnly && <Tab value={SECTION_CHAT} icon={<ChatIcon fontSize="small" />} iconPosition="start" label="Chat" sx={{ minHeight: 48, fontSize: '0.75rem' }} />}
				</Tabs>}

				{/* Properties Tab - Always mounted to keep state updated */}
				<ActionPanelTabContent active={displayedActiveSection === SECTION_PROPERTIES} innerBoxSx={{ overflowY: 'auto' }}>
					<AppItemProperties
						key={selectedItem?.id || 'none'}
						bundle={bundle}
						selectedItem={selectedItem}
						selectedComponentPath={selectedComponentPath}
						selectedSharedComponentContext={selectedSharedComponentContext}
						reactComponents={reactComponents}
						variableScope={variableScope}
						onBundleChanged={onBundleChanged}
						onComponentChanged={onComponentChanged}
						onMoveComponentUp={onMoveComponentUp}
						onMoveComponentDown={onMoveComponentDown}
						onRemoveComponent={onRemoveComponent}
						onSelectedComponentPathChanged={onSelectedComponentPathChanged}
					/>
				</ActionPanelTabContent>

				{/* Chat Tab */}
				{!readOnly && <ActionPanelTabContent active={displayedActiveSection === SECTION_CHAT} innerBoxSx={{ display: 'flex', flexDirection: 'column', p: 0 }}>
					{selectedReactComponentName && (
						<Alert severity="info" icon={<InfoIcon fontSize="small" />} sx={{ borderRadius: 0, py: 0.25 }}>
							<Typography variant="caption">
								React target: <strong>{selectedReactComponentName}</strong>
							</Typography>
						</Alert>
					)}
					{selectedReactSourceFile && (
						<Alert severity="info" icon={<InfoIcon fontSize="small" />} sx={{ borderRadius: 0, py: 0.25 }}>
							<Typography variant="caption">
								React source: <strong>{selectedReactSourceFile.path}</strong>
							</Typography>
						</Alert>
					)}
					<PromptChat
						headerPrimaryActions={definitionId && changeSetId ? <AppCheckpointActions
							key={`${definitionId}:${changeSetId}`} definitionId={definitionId} changeSetId={changeSetId}
							hasUnsavedChanges={hasUnsavedChanges} agentBusy={chatBusy} onRestored={handleCheckpointRestored}
						/> : undefined}
						onStatusChange={handleChatStatus}
						onComplete={refreshCheckpoints}
						adapter={chatAdapter}
						defaultTitle="Randol App Assistant"
						showConversationNameBar
						emptyStateText="Describe the app, view, component, resource, or visual style you want to create or refine."
						suggestedPrompts={USER_INTERFACE_CHAT_SUGGESTED_PROMPTS}
						onAssistantMessageReceived={handleAssistantMessageReceived}
					/>
				</ActionPanelTabContent>}

				{/* Add Component Tab - Always mounted to keep state updated */}
				<ActionPanelTabContent active={displayedActiveSection === SECTION_ADD_COMPONENT} innerBoxSx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
					{selectedItem?.type === ITEM_TYPE_SETTINGS && (
						<Alert severity="info" icon={<InfoIcon fontSize="small" />} sx={{ py: 0.5 }}>
							<Typography variant="caption">Select Properties to edit bundle settings.</Typography>
						</Alert>
					)}
					{addTargetInfo.canAdd && (
						<Alert severity="info" icon={<InfoIcon fontSize="small" />} sx={{ py: 0.5 }}>
							<Typography variant="caption">
								{addTargetInfo.targetType === 'selected' && (
									<>
										Will add to selected: <strong>{addTargetInfo.targetName}</strong>
									</>
								)}
								{addTargetInfo.targetType === 'parent' && (
									<>
										Will add as sibling in: <strong>{addTargetInfo.targetName}</strong>
									</>
								)}
								{addTargetInfo.targetType === 'root' && (
									<>
										Will add to: <strong>{addTargetInfo.targetName}</strong>
									</>
								)}
							</Typography>
						</Alert>
					)}
					{!addTargetInfo.canAdd && selectedItem?.type !== ITEM_TYPE_SETTINGS && (
						<Alert severity="warning" icon={<InfoIcon fontSize="small" />} sx={{ py: 0.5 }}>
							<Typography variant="caption">No valid container found. Select a component within a container to add new components.</Typography>
						</Alert>
					)}
					<ListWithFilter
						items={components}
						onSelected={addTargetInfo.canAdd ? onAddComponent : () => {}}
						placeholder="Search components"
						getDisplayName={(item) => item.displayName}
						getDescription={(item) => item.description}
						showDescriptionToggle
						getGroup={(item) => item.category}
						getKey={(item) => item.name}
						icon={(item) => <IconComponent iconName={item.iconName} iconUrl={item.iconUrl} />}
						settingsKey="uiActionPanel"
						sx={{ flex: 1, minHeight: 0 }}
					/>
				</ActionPanelTabContent>
			</Stack>
		</Paper>
	);
}
